import { Button, Dropdown, Input } from '@/shared';
import { useEffect, useRef, useState } from 'react';
import { useDebounce } from '@/shared/hooks/useDebounce';

export interface ResourceFiltersType {
  search: string;
  page: number;
  limit: number;
  category?: string;
}

const categoryOptions = [
  { label: 'All Categories', value: '' },
  { label: 'Guides', value: 'GUIDE' },
  { label: 'Policies', value: 'POLICY_DOCUMENT' },
  { label: 'Templates', value: 'TEMPLATE' },
  { label: 'External Links', value: 'EXTERNAL_LINK' },
];

interface ResourceFiltersProps {
  filters: ResourceFiltersType;
  setFilters: React.Dispatch<React.SetStateAction<ResourceFiltersType>>;
  totalRecords?: number;
}

export const ResourceFilters: React.FC<ResourceFiltersProps> = ({
  filters,
  setFilters,
  totalRecords = 0,
}) => {
  const [showFilters, setShowFilters] = useState(false);
  const filterRef = useRef<HTMLDivElement>(null);

  const [localSearch, setLocalSearch] = useState(filters.search);
  const debouncedSearch = useDebounce(localSearch, 500);

  // Sync local search with filters when filters change externally
  useEffect(() => {
    setLocalSearch(filters.search);
  }, [filters.search]);

  // Update filters when debounced search changes
  useEffect(() => {
    if (debouncedSearch !== filters.search) {
      setFilters((prev) => ({
        ...prev,
        search: debouncedSearch,
        page: 1,
      }));
    }
  }, [debouncedSearch, setFilters, filters.search]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLocalSearch(e.target.value);
  };

  const clearFilterHandler = () => {
    setFilters({
      search: '',
      page: 1,
      limit: 10,
      category: '',
    });
  };

  // === OUTSIDE CLICK HANDLER ===
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        filterRef.current &&
        !filterRef.current.contains(event.target as Node)
      ) {
        setShowFilters(false);
      }
    };

    if (showFilters) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showFilters]);

  // Handle category change
  const handleCategoryChange = (value: unknown) => {
    setFilters((prev) => ({
      ...prev,
      category: value as string,
      page: 1,
    }));
  };

  return (
    <div className="w-full">
      <div className="flex flex-col lg:flex-row items-center justify-between">
        {/* Title */}
        <div className="mb-4">
          <p className="text-lg md:text-2xl text-text-primary font-bold">
            All documents
            <span className="font-normal text-xl">({totalRecords})</span>
          </p>
        </div>

        {/* Filters */}
        <div className="w-full md:w-auto flex flex-col md:flex-row items-center gap-4">
          {/* Search */}
          <div className="flex items-center w-full md:w-[256px]">
            <Input
              className="rounded-lg !w-full !text-[16px]"
              placeholder="Search resources"
              value={localSearch}
              onChange={handleSearchChange}
              rightIcon={
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 18 18"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M16.6 18L10.3 11.7C9.8 12.1 9.225 12.4167 8.575 12.65C7.925 12.8833 7.23333 13 6.5 13C4.68333 13 3.14583 12.3708 1.8875 11.1125C0.629167 9.85417 0 8.31667 0 6.5C0 4.68333 0.629167 3.14583 1.8875 1.8875C3.14583 0.629167 4.68333 0 6.5 0C8.31667 0 9.85417 0.629167 11.1125 1.8875C12.3708 3.14583 13 4.68333 13 6.5C13 7.23333 12.8833 7.925 12.65 8.575C12.4167 9.225 12.1 9.8 11.7 10.3L18 16.6L16.6 18ZM6.5 11C7.75 11 8.8125 10.5625 9.6875 9.6875C10.5625 8.8125 11 7.75 11 6.5C11 5.25 10.5625 4.1875 9.6875 3.3125C8.8125 2.4375 7.75 2 6.5 2C5.25 2 4.1875 2.4375 3.3125 3.3125C2.4375 4.1875 2 5.25 2 6.5C2 7.75 2.4375 8.8125 3.3125 9.6875C4.1875 10.5625 5.25 11 6.5 11Z"
                    fill="#474747"
                  />
                </svg>
              }
            />
          </div>

          {/* Filter Button */}
          <div className="relative w-full md:w-auto" ref={filterRef}>
            <div
              className="w-full flex items-center justify-end md:justify-start gap-1 cursor-pointer card p-3 text-text-secondary"
              onClick={() => setShowFilters((prev) => !prev)}
            >
              <svg
                width="18"
                height="12"
                viewBox="0 0 18 12"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M7 12V10H11V12H7ZM3 7V5H15V7H3ZM0 2V0H18V2H0Z"
                  fill="#474747"
                />
              </svg>
              <span>Filter</span>
            </div>

            {/* Filter Panel */}
            {showFilters && (
              <div className="absolute top-12 right-0 z-50 shadow-xl shadow-gray-300 animate-slideDown">
                <div className="card min-w-80 p-4">
                  {/* Category Filter */}
                  <div className="mb-4 w-full">
                    <p className="text-sm font-medium text-text-secondary mb-2">
                      Category
                    </p>
                    <Dropdown
                      options={categoryOptions}
                      value={filters.category || ''}
                      onChange={handleCategoryChange}
                      className="w-full"
                      buttonClassName="w-full"
                    />
                  </div>

                  {/* Clear Filters */}
                  <div className="flex justify-end">
                    <Button
                      onClick={clearFilterHandler}
                      variant="outline"
                      size="sm"
                      className="w-full"
                    >
                      Clear Filters
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
